import React from 'react'
import PropTypes from 'prop-types'
import { FormattedMessage } from 'react-intl'
import { connect } from 'react-redux'
import { Link, withRouter } from 'react-router-dom'
import { withLocale, localeShape } from '../../locale/withLocale'
import { logOut, selectIsLoggedIn } from '../../store/userSlice'
import { CreateLink } from '../../utils'
import { getBlogCategories, searchBlogPosts } from '../../wpapi'
import LanguageSwitcher from '../LanguageSwitcher/LanguageSwitcher'
import messages from './messages'
import { trackStartRegistration } from '../../analytics'
import loadable from '@loadable/component'
import useMobileMenu from '../../hooks/useMobileMenu'
import classNames from 'classnames'

import Hamburger from '!!@svgr/webpack!media/hamburger.svg'
import LogoShort from '!!@svgr/webpack!media/logo-short.svg'
import Logo from '!!@svgr/webpack!media/logo.svg'

const MegaMenu = loadable(() =>
  import(/* webpackChunkName: "MegaMenu" */ '../MegaMenu/MegaMenu.js')
)

const LoginOverlay = loadable(() => import('../Login/LoginOverlay'))

const HeaderSearchResults = loadable(() => import('./HeaderSearchResults'))

function MenuButton({ dark }) {
  const { open } = useMobileMenu()

  return (
    <button
      className="c-menu-bar__link  c-off-canvas__open  lg:hidden"
      onClick={open}
      data-uid="menu_header_button"
    >
      <Hamburger
        className={classNames('c-off-canvas__open-icon', {
          'c-off-canvas__open-icon--dark': dark,
        })}
        id="hamburger"
      />
      <span>
        <FormattedMessage {...messages.menu} />
      </span>
    </button>
  )
}

MenuButton.propTypes = {
  dark: PropTypes.bool,
}

class Header extends React.Component {
  articlesButton = React.createRef()
  searchField = React.createRef()

  static propTypes = {
    blog: PropTypes.bool,
    loading: PropTypes.bool,
    sticky: PropTypes.bool,
    inverted: PropTypes.bool,
    onTopOfGreen: PropTypes.bool,
    onTopOfBlue: PropTypes.bool,
    onTopOfMagenta: PropTypes.bool,
    premium: PropTypes.bool,
    dispatch: PropTypes.func,
    history: PropTypes.object,
    logoOnly: PropTypes.bool,
    locale: localeShape,
    isLoggedIn: PropTypes.bool,
    home: PropTypes.bool,
  }

  static defaultProps = {
    inverted: true,
    premium: false,
    logoOnly: false,
    sticky: true,
  }

  constructor(props) {
    super(props)

    this.state = {
      open: false,
      menuOpen: false,
      subMenu: false,
      megaMenuOpen: false,
      hits: false,
      showLoginOverlay: false,
    }

    this.showBlogSearch = this.showBlogSearch.bind(this)
    this.performSearch = this.performSearch.bind(this)
    this.hideResults = this.hideResults.bind(this)
    this.showResults = this.showResults.bind(this)
    this.browseResults = this.browseResults.bind(this)
    this.showMegaMenu = this.showMegaMenu.bind(this)
    this.hideMegaMenu = this.hideMegaMenu.bind(this)
    this.handleScroll = this.handleScroll.bind(this)
  }

  componentDidMount() {
    if (this.props.blog && !this.props.loading) {
      getBlogCategories().then(categories => {
        let cleanedCategories = categories

        // Remove unwanted categories
        for (let category of categories) {
          if (category.name === 'All' || category.name === 'Uncategorized') {
            cleanedCategories.splice(cleanedCategories.indexOf(category), 1)
          }
        }

        this.setState({
          subMenu: cleanedCategories,
        })
      })
    }

    this.timestamp = new Date()
    window.addEventListener('scroll', this.handleScroll)
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.handleScroll)
  }

  handleScroll() {
    if (this.props.sticky) {
      const timestamp = new Date()
      const toggleFloat = () => {
        const scrollingElement =
          document.scrollingElement || document.documentElement

        this.setState({
          floating: this.state.showBlogSearch
            ? false
            : scrollingElement.scrollTop > 0,
        })
      }

      // Check at most every 50 ms
      if (timestamp - this.timestamp > 50) {
        toggleFloat()
        this.timestamp = timestamp
        // Queue event
      } else if (!this.queuedScrollEvt) {
        this.queuedScrollEvt = true
        setTimeout(() => {
          toggleFloat()
          this.queuedScrollEvt = false
        }, timestamp - this.timestamp)
        this.timestamp = timestamp
      }
    }
  }

  setMenuClasses() {
    return classNames('c-menu-bar c-header__menu', {
      'c-menu-bar--inverted': this.props.inverted && !this.state.floating,
      'c-menu-bar--white-hocus':
        this.props.onTopOfGreen && !this.state.floating,
      'c-menu-bar--white-hocus c-menu-bar--white-hocus-magenta':
        this.props.onTopOfMagenta && !this.state.floating,
      'c-menu-bar--white-hocus c-menu-bar--white-hocus-blue':
        this.props.onTopOfBlue && !this.state.floating,
      'c-menu-bar--premium-theme': this.props.premium && !this.state.floating,
    })
  }

  logOut = e => {
    e.preventDefault()
    this.props.dispatch(logOut())
    this.props.history.push('/')
  }

  showBlogSearch(e) {
    e.preventDefault()
    this.setState(
      {
        showBlogSearch: !this.state.showBlogSearch,
        hits: false,
        searching: false,
      },
      () => {
        if (this.searchField.current) {
          this.searchField.current.focus()
          this.searchField.current.addEventListener('focus', this.showResults)
        } else {
          document
            .querySelector('html')
            .removeEventListener('click', this.hideResults)
          document
            .querySelector('html')
            .removeEventListener('keydown', this.browseResults)
        }
      }
    )
  }

  performSearch(e) {
    e.preventDefault()

    if (this.searchField.current.value === '') {
      return
    }

    this.setState(
      {
        searching: true,
        hits: false,
      },
      () => {
        let term = this.searchField.current.value.toLowerCase()

        searchBlogPosts(encodeURIComponent(term)).then(posts => {
          if (!posts.length) {
            this.setState({
              hits: [],
              searching: false,
            })
          } else {
            this.sortSearchResults(term, posts).then(results => {
              // Highlight each word individually to allow for more highlights
              const terms = term.indexOf(' ') > -1 ? term.split(' ') : [term]

              results.forEach(result => {
                terms.forEach(term => {
                  const capitalized =
                    term.charAt(0).toUpperCase() + term.slice(1)
                  result.title.rendered = result.title.rendered.replace(
                    term,
                    '<b>' + term + '</b>'
                  )
                  result.title.rendered = result.title.rendered.replace(
                    capitalized,
                    '<b>' + capitalized + '</b>'
                  )
                  result.excerpt.rendered = result.excerpt.rendered.replace(
                    term,
                    '<b>' + term + '</b>'
                  )
                  result.excerpt.rendered = result.excerpt.rendered.replace(
                    capitalized,
                    '<b>' + capitalized + '</b>'
                  )
                })
              })

              this.setState({
                showSearchResults: true,
                hits: results,
                searching: false,
              })
            })
          }

          document
            .querySelector('html')
            .addEventListener('click', this.hideResults)
          document
            .querySelector('html')
            .addEventListener('keydown', this.browseResults)
        })
      }
    )
  }

  hideResults(e) {
    if (
      e.target !== this.searchField &&
      !e.target.parentNode.closest('.c-header__search-results')
    ) {
      this.setState({
        showSearchResults: false,
      })
    }
  }

  showResults() {
    this.setState({
      showSearchResults: true,
    })
  }

  showMegaMenu() {
    if (!this.state.megaMenuOpen) {
      this.setState({
        megaMenuOpen: true,
      })
    }
  }

  hideMegaMenu() {
    if (this.state.megaMenuOpen) {
      this.setState(
        {
          megaMenuOpen: false,
        },
        () => {
          this.articlesButton.current.focus()
        }
      )
    }
  }

  handleLoginClick = () => {
    this.setState({ showLoginOverlay: true })
  }

  handleLoginClose = () => {
    this.setState({ showLoginOverlay: false })
  }

  browseResults(e) {
    if (e.keyCode === 40) {
      // Arrow down
      e.preventDefault()
      if (e.target === this.searchField.current) {
        document.querySelector('.c-header__search-result-link').focus()
      } else if (e.target.className === 'c-header__search-result-link') {
        const next = e.target && e.target.parentNode.nextSibling

        if (next) {
          next.querySelector('a').focus()
        } else {
          document.querySelector('.c-header__search-result-link').focus()
        }
      }
    } else if (e.keyCode === 38) {
      // Arrow up
      e.preventDefault()
      if (e.target.className === 'c-header__search-result-link') {
        const prev = e.target && e.target.parentNode.previousSibling

        if (prev) {
          prev.querySelector('a').focus()
        } else {
          const query = document.querySelectorAll(
            '.c-header__search-result-link'
          )
          query[query.length - 1].focus()
        }
      }
    }
  }

  sortSearchResults(term, posts) {
    return new Promise(resolve => {
      posts.forEach(post => {
        let score = 0

        // Make array with full term, followed by individual
        // words in the term.
        let terms = [term]
        if (term.indexOf(' ') > -1) {
          term.split(' ').forEach(term => {
            terms.push(term)
          })
        }

        terms.forEach(term => {
          // We want to double the score is the full term is found
          const isFullTerm = term === terms[0]

          // If found in title, high score
          if (post.title.rendered.indexOf(term) > -1) {
            score += isFullTerm ? 0.1 : 0.05
          }

          // If found in excerpt, lower score
          if (post.excerpt.rendered.indexOf(term) > -1) {
            score += isFullTerm ? 0.05 : 0.025
          }

          // Term frequency in content
          //
          // Number of times the term appears in the text
          // divided by total words in the text
          if (post.content.rendered.indexOf(term) > -1) {
            const words = post.content.rendered.split(' ').length
            const occurancesOfTerm =
              post.content.rendered.split(term).length - 1
            const frequency = occurancesOfTerm / words

            score += isFullTerm ? frequency * 2 : frequency
          }
        })

        post.score = score
      })

      // Now that all posts have a score, sort them
      posts.sort((a, b) => {
        return b.score - a.score
      })

      resolve(posts)
    })
  }

  loadPage(e) {
    document.location.href = e.currentTarget.getAttribute('data-href')
  }

  render = () => (
    <header
      className={classNames('c-header z-30', {
        'c-header--floating': this.state.floating,
        'c-header--stuck': this.state.showBlogSearch || !this.props.sticky,
      })}
      role="banner"
      onMouseOver={() => MegaMenu.preload()}
    >
      <div className="o-width-limiter o-width-limiter--full">
        <div className="c-header__inner">
          <div className={this.setMenuClasses()}>
            {!this.props.logoOnly && (
              <nav>
                <MenuButton
                  dark={
                    this.props.inverted ||
                    this.props.onTopOfGreen ||
                    this.props.onTopOfMagenta ||
                    this.props.premium
                      ? this.state.floating
                        ? true
                        : false
                      : true
                  }
                />
                <ul className="c-menu-bar__list  hidden  lg:flex">
                  <li className="c-menu-bar__item">
                    {/* <Link
                    to={`/${this.state.locale}/${Slugs['features'][
                      this.state.locale
                    ]}`}
                    className="c-menu-bar__link"
                  > */}
                    <Link
                      to={CreateLink('features', this.props.locale.language)}
                      className="c-menu-bar__link"
                    >
                      <span className="c-menu-bar__link-text">
                        <FormattedMessage {...messages.features} />
                      </span>
                    </Link>
                  </li>
                  {this.props.locale.language === 'en' && (
                    <li
                      className={
                        this.props.inverted
                          ? this.state.floating
                            ? 'c-menu-bar__item c-menu-bar__item--inverted'
                            : 'c-menu-bar__item'
                          : 'c-menu-bar__item c-menu-bar__item--inverted'
                      }
                    >
                      <button
                        onClick={this.showMegaMenu}
                        className={
                          this.state.megaMenuOpen
                            ? 'c-menu-bar__link c-menu-bar__link--arrow c-menu-bar__link--menu-open'
                            : 'c-menu-bar__link c-menu-bar__link--arrow'
                        }
                        ref={this.articlesButton}
                      >
                        <span className="c-menu-bar__link-text">Articles</span>
                      </button>
                    </li>
                  )}
                  <li className="c-menu-bar__item">
                    <a href="/blog/" className="c-menu-bar__link">
                      <span className="c-menu-bar__link-text">
                        <FormattedMessage {...messages.blog} />
                      </span>
                    </a>
                    {this.state.subMenu && (
                      <ul className="c-menu-bar__sub-menu">
                        {this.state.subMenu.map(item => {
                          return (
                            <li
                              key={item.id}
                              className="c-menu-bar__sub-menu-item"
                            >
                              <button
                                data-href={'/blog/category/' + item.slug + '/'}
                                className="c-menu-bar__sub-menu-link"
                                onClick={this.loadPage}
                              >
                                {item.name}
                              </button>
                            </li>
                          )
                        })}
                        <li className="c-menu-bar__sub-menu-item">
                          <button
                            data-href="/blog/tags/recipe/"
                            className="c-menu-bar__sub-menu-link"
                            onClick={this.loadPage}
                          >
                            Recipes
                          </button>
                        </li>
                        <li className="c-menu-bar__sub-menu-item">
                          <button
                            className={
                              this.state.showBlogSearch
                                ? 'c-menu-bar__sub-menu-link c-menu-bar__sub-menu-link--close'
                                : 'c-menu-bar__sub-menu-link'
                            }
                            onClick={this.showBlogSearch}
                          >
                            Search
                          </button>
                        </li>
                      </ul>
                    )}
                  </li>
                </ul>
              </nav>
            )}
          </div>
          <Link
            data-uid="home_header_logo"
            to={CreateLink('', this.props.locale.language)}
            className={
              this.state.floating
                ? 'c-header__logo c-header__logo--short'
                : 'c-header__logo'
            }
          >
            <span className="visually-hidden">Lifesum.com start page</span>
            {this.state.floating && (
              <LogoShort className="c-logo c-logo--short" id="logo-short" />
            )}
            {!this.state.floating && (
              <Logo
                className={
                  this.props.inverted
                    ? 'c-logo c-logo--header c-logo--inverted'
                    : 'c-logo'
                }
                id="logo"
              />
            )}
          </Link>
          <div className={this.setMenuClasses() + ' flex'}>
            {!this.props.logoOnly && (
              <nav>
                <ul className="c-menu-bar__list  hidden md:flex">
                  <li className="c-menu-bar__item   hidden  lg:block">
                    <LanguageSwitcher
                      desktop={true}
                      inverted={
                        this.state.floating ? true : !this.props.inverted
                      }
                    />
                  </li>
                  {this.props.isLoggedIn && (
                    <li className="c-menu-bar__item">
                      <Link
                        data-uid="account_header_button"
                        className="c-menu-bar__link"
                        to={CreateLink('account', this.props.locale.language)}
                      >
                        <span className="c-menu-bar__link-text">
                          <FormattedMessage {...messages.account} />
                        </span>
                      </Link>
                    </li>
                  )}
                  {this.props.isLoggedIn && (
                    <li className="c-menu-bar__item  hidden  lg:block">
                      <a
                        data-uid="logout_header_button"
                        className="c-menu-bar__link"
                        href="/"
                        onClick={this.logOut}
                      >
                        <span className="c-menu-bar__link-text">
                          <FormattedMessage {...messages.logOut} />
                        </span>
                      </a>
                    </li>
                  )}
                  {!this.props.isLoggedIn && (
                    <li className="c-menu-bar__item  hidden  lg:block">
                      <button
                        type="button"
                        data-uid="login_header_button"
                        className="c-menu-bar__link"
                        onClick={this.handleLoginClick}
                      >
                        <span className="c-menu-bar__link-text">
                          <FormattedMessage {...messages.logIn} />
                        </span>
                      </button>
                    </li>
                  )}
                  {!this.props.isLoggedIn && !this.props.home && (
                    <li className="c-menu-bar__item">
                      <Link
                        data-uid="signup_header_button"
                        className="c-menu-bar__button"
                        to={CreateLink('signup', this.props.locale.language)}
                        onClick={() => {
                          trackStartRegistration({
                            entryPoint: 'Header',
                            flowName: 'Regular',
                          })
                        }}
                      >
                        <span className="c-menu-bar__link-text">
                          <FormattedMessage {...messages.signUp} />
                        </span>
                      </Link>
                    </li>
                  )}
                  {!this.props.isLoggedIn && this.props.home && (
                    <li className="c-menu-bar__item  hidden  lg:block">
                      <Link
                        data-uid="signup_header_button"
                        className="c-menu-bar__button"
                        to={CreateLink('signup', this.props.locale.language)}
                        onClick={() => {
                          trackStartRegistration({
                            entryPoint: 'Header',
                            flowName: 'Regular',
                          })
                        }}
                      >
                        <span className="c-menu-bar__link-text">
                          <FormattedMessage {...messages.signUp} />
                        </span>
                      </Link>
                    </li>
                  )}
                </ul>
              </nav>
            )}
            {this.state.subMenu && (
              <div className="c-menu-bar__search-btn-wrapper  lg:hidden">
                <button
                  className={
                    this.state.showBlogSearch
                      ? 'c-menu-bar__link  c-menu-bar__search-btn c-menu-bar__search-btn--close'
                      : 'c-menu-bar__link  c-menu-bar__search-btn'
                  }
                  onClick={this.showBlogSearch}
                >
                  <span className="c-menu-bar__link-text">Search</span>
                </button>
              </div>
            )}
          </div>
        </div>
        {this.props.locale.language === 'en' && this.state.megaMenuOpen && (
          <MegaMenu
            open={this.state.megaMenuOpen}
            desktop={true}
            onOutsideClick={this.hideMegaMenu}
          />
        )}
        {this.state.showBlogSearch && (
          <div className="c-header__search">
            <form onSubmit={this.performSearch}>
              <input
                className="c-header__search-field"
                type="search"
                ref={this.searchField}
              />
              <input
                type="submit"
                value="Go"
                className={
                  this.state.searching
                    ? 'c-header__search-button c-header__search-button--searching'
                    : 'c-header__search-button'
                }
              />
            </form>
            {this.state.hits &&
              this.state.hits.length > 0 &&
              this.state.showSearchResults && (
                <HeaderSearchResults hits={this.state.hits} />
              )}
            {this.state.hits && !this.state.hits.length && (
              <div className="c-header__search-no-hits">
                <img src={require('media/apple-sad.png')} alt="Sad apple" />
                <p>
                  No posts related to ”{this.searchField.current.value}” found.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
      {this.state.showLoginOverlay && (
        <LoginOverlay onClose={this.handleLoginClose} />
      )}
    </header>
  )
}

export default connect(state => ({
  isLoggedIn: selectIsLoggedIn(state),
}))(withLocale(withRouter(Header)))
